/**
 * FormPage (TMMPDM-0036, DLG-002 / FRM-001 / FRM-005 / FRM-006 / FRM-008 / FRM-011 / A11Y-010) — the
 * layout of every editor page: a `PageHeader` whose breadcrumbs and title name the record being
 * changed, the form in one readable column (max 720 px) grouped into `FormSection`s, and a sticky
 * action bar at the bottom of the viewport — Cancel, any secondary action, then the verb-labelled
 * `SubmitButton` on the right.
 *
 * - An optional `aside` (a live preview) sits to the right of the form from 1280 px and under it
 *   below that.
 * - `errorSummary`: after a submit that found errors the page passes the fields that need attention
 *   and bumps `attempt`; the summary ("2 fields need attention", a link per field that focuses it)
 *   takes focus. Errors are never shown before the first submit — the page decides when to pass
 *   them.
 * - `guard` (`useUnsavedChangesGuard`) supplies the Leave / Stay prompt; Cancel navigates through it.
 *
 * - `headerless` (TMM-B7): the page renders its own `PageHeader` above this form in every state of
 *   the page (loading, failed, not editable, loaded), so its `h1` is one element from the first
 *   render to loaded and the late title is announced once. `breadcrumbs` and `help` then belong to
 *   that header.
 *
 * A native `<form>`: Enter in a single-line field submits, and the submit button is the only one
 * with `type="submit"`.
 */
import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Link, Stack } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type { UnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { PageHeader } from './PageHeader';
import { SectionCard } from './SectionCard';
import { SubmitButton } from './SubmitButton';
import type { Crumb } from './headerChrome';

export interface ErrorSummaryItem {
  /** The id of the control the link focuses. */
  id: string;
  label: string;
  message: string;
}

export interface ErrorSummary {
  items: ErrorSummaryItem[];
  /** Bumped on every submit that found errors; each bump moves focus to the summary. */
  attempt: number;
}

export interface FormPageProps {
  /** The header's trail; the page's own header carries it when `headerless`. */
  breadcrumbs?: Crumb[];
  title: string;
  subtitle?: string;
  /** The title's field-help "?". */
  help?: ReactNode;
  /** The page renders its own header above the form (see above); this one draws none. */
  headerless?: boolean;
  onSubmit: () => void;
  busy: boolean;
  submitLabel: string;
  onCancel: () => void;
  guard: UnsavedChangesGuard;
  errorSummary?: ErrorSummary;
  /** A page-level problem that belongs to no field (a 409, a network failure). */
  formError?: ReactNode;
  /** Secondary actions for the bar, between Cancel and the submit button (Check). */
  actions?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * Focus the control behind `id`. A MUI select's id lands on its hidden native input, so focus goes
 * to the select's combobox beside it instead.
 */
function focusField(id: string) {
  const element = document.getElementById(id);
  if (!element) return;
  const combobox =
    element instanceof HTMLInputElement && element.getAttribute('aria-hidden') === 'true'
      ? element.parentElement?.querySelector<HTMLElement>('[role="combobox"]')
      : null;
  (combobox ?? element).focus();
}

/** The preview panel sits beside the form from this viewport width (§34 item 10). */
const WIDE = '@media (min-width:1280px)';

export const FORM_MAX_WIDTH = 720;

export function FormPage({
  breadcrumbs,
  title,
  subtitle,
  help,
  headerless = false,
  onSubmit,
  busy,
  submitLabel,
  onCancel,
  guard,
  errorSummary,
  formError,
  actions,
  aside,
  children,
}: FormPageProps) {
  const { t } = useCopy();
  const summaryRef = useRef<HTMLDivElement>(null);
  const attempt = errorSummary?.attempt ?? 0;
  const count = errorSummary?.items.length ?? 0;

  // Focus follows each failed submit to the summary (A11Y-010) — never on a keystroke.
  useEffect(() => {
    if (attempt > 0) summaryRef.current?.focus();
  }, [attempt]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onSubmit();
  };

  return (
    <Box component="form" noValidate onSubmit={submit} aria-label={title}>
      {headerless ? null : (
        <PageHeader
          {...(breadcrumbs !== undefined ? { breadcrumbs } : {})}
          title={title}
          {...(subtitle !== undefined ? { subtitle } : {})}
          {...(help !== undefined ? { help } : {})}
        />
      )}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 3, [WIDE]: { flexDirection: 'row' } }}
      >
        <Stack
          spacing={3}
          sx={{ width: '100%', maxWidth: FORM_MAX_WIDTH, minWidth: 0, flexShrink: 0 }}
        >
          {errorSummary && count > 0 ? (
            <Alert severity="error" ref={summaryRef} tabIndex={-1} data-error-summary="">
              <AlertTitle>
                {t('components.errorSummary', '{{count}} fields need attention', { count })}
              </AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {errorSummary.items.map((item) => (
                  <li key={item.id}>
                    <Link
                      component="button"
                      type="button"
                      underline="always"
                      color="inherit"
                      onClick={() => focusField(item.id)}
                      sx={{ textAlign: 'left', verticalAlign: 'baseline' }}
                    >
                      {item.label}
                    </Link>
                    : {item.message}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : null}
          {formError}
          {children}
        </Stack>
        {aside ? <Box sx={{ flex: 1, minWidth: 0 }}>{aside}</Box> : null}
      </Box>
      <Box
        data-form-actions=""
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          mx: -1,
          mt: 3,
          px: 1,
          py: 1.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', maxWidth: FORM_MAX_WIDTH }}
        >
          <Button onClick={onCancel} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {actions}
          <SubmitButton type="submit" busy={busy}>
            {submitLabel}
          </SubmitButton>
        </Stack>
      </Box>
      {guard.dialog}
    </Box>
  );
}

/**
 * One group of the form under an `h2` (FRM-001), on a `SectionCard`.
 * `help` sits in the card's actions (top-right), not inside the heading.
 */
export function FormSection({
  title,
  help,
  children,
}: {
  title: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SectionCard title={title} {...(help != null ? { actions: help } : {})}>
      <Stack spacing={2}>{children}</Stack>
    </SectionCard>
  );
}

/** A field sized to its content (FRM-006): `width` in `ch`, full width on a narrow screen. */
export function FormField({ width, children }: { width: string; children: ReactNode }) {
  return <Box sx={{ width: '100%', maxWidth: width }}>{children}</Box>;
}

/** Short related fields side by side (FRM-010) — wraps when they don't fit. */
export function FormRow({ children }: { children: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      useFlexGap
      sx={{ flexWrap: 'wrap', '& > *': { flex: '1 1 0' } }}
    >
      {children}
    </Stack>
  );
}
